import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { ApiHttpError, apiFetch } from './api'

export type Role = 'EMPLOYEE' | 'ADMIN'

export interface SessionUserPosition {
  id: string
  name: string
  sortOrder: number
}

export interface SessionUser {
  id: string
  email: string
  name: string
  role: Role
  mustChangePassword: boolean
  rodoSeenAt: string | null
  position: SessionUserPosition | null
  hasChildrenUnder14: boolean
}

export const SESSION_QUERY_KEY = ['session'] as const

export function useSession() {
  return useQuery<SessionUser | null>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        const data = await apiFetch<{ user: SessionUser }>('/api/auth/me')
        return data.user
      } catch (err) {
        if (err instanceof ApiHttpError && err.status === 401) return null
        throw err
      }
    },
    staleTime: 60_000,
    retry: false,
    // Stan logowania nie może przeżyć w pamięci dłużej niż sesja na serwerze.
    // 'always' — bez tego zdarzenie `offline` wstrzymuje zapytanie i ekran
    // dalej pokazuje użytkownika, którego serwer już nie zna.
    networkMode: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

/**
 * Serwer odpowiedział 401 — RequireAuth odeśle na /login. Wołane tylko przy
 * zapytaniach i zapisach czasu pracy, nigdy z ogólnego handlera mutacji:
 * login odpowiada 401 na złe hasło, więc taki handler wylogowywałby za literówkę.
 */
export function markLoggedOut(qc: QueryClient) {
  qc.setQueryData(SESSION_QUERY_KEY, null)
}

/**
 * Wszystko poza sesją należy do konkretnej osoby. Po wylogowaniu i po
 * zalogowaniu znika, żeby na wspólnym telefonie nikt nie zobaczył cudzego
 * stanu pracy (ani przez chwilę nie dostał przycisku ustawionego pod nim).
 */
export function dropUserScopedQueries(qc: QueryClient) {
  qc.removeQueries({
    predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<null>('/api/auth/logout', { method: 'POST' }),
    onSettled: () => {
      dropUserScopedQueries(qc)
      qc.setQueryData(SESSION_QUERY_KEY, null)
    },
  })
}

export function landingFor(user: SessionUser): string {
  if (user.mustChangePassword) return '/change-password'
  return user.role === 'ADMIN' ? '/admin' : '/'
}
